import { useEffect, useRef, useState } from "react";
import { Loader2, Upload, X, Check, Palette, Pencil } from "lucide-react";
import { http, errText } from "@/lib/http";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Panel, Eyebrow } from "@/components/mh/bits";

const ACCENTS = ["#D4FF3A", "#7000FF", "#00E676", "#FFAB00", "#FF1744", "#2979FF", "#00E5FF"];

export default function BrandEditor() {
  const { current, isOwner, isAdmin, reload } = useAuth();
  const canEdit = isOwner || isAdmin;
  const [ws, setWs] = useState(null);
  const [name, setName] = useState("");
  const [tagline, setTagline] = useState("");
  const [accent, setAccent] = useState("#D4FF3A");
  const [logoUrl, setLogoUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!current) return;
    http.get("/workspaces/current").then((r) => {
      setWs(r.data);
      setName(r.data.name || "");
      setTagline((r.data.brand || {}).tagline || "");
      setAccent((r.data.brand || {}).accent_hex || "#D4FF3A");
      setLogoUrl(r.data.logo_url || "");
      setDirty(false);
    }).catch(() => {});
  }, [current?.id]);

  const save = async () => {
    if (!ws) return;
    setSaving(true);
    try {
      const r = await http.put(`/workspaces/${ws.id}`, { name: name.trim(), tagline, accent_hex: accent, logo_url: logoUrl || null });
      toast.success("Workspace updated");
      setWs(r.data);
      setDirty(false);
      await reload();
    } catch (e) { toast.error(errText(e, "Could not save")); }
    setSaving(false);
  };

  const upload = async (file) => {
    if (!file || !ws) return;
    if (!file.type.startsWith("image/")) { toast.error("Logo must be an image"); return; }
    if (file.size > 4 * 1024 * 1024) { toast.error("Logo must be 4MB or smaller"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await http.post(`/workspaces/${ws.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoUrl(r.data.logo_url);
      toast.success("Logo updated");
      await reload();
    } catch (e) { toast.error(errText(e, "Logo upload failed")); }
    setUploading(false);
  };

  const removeLogo = async () => {
    if (!ws) return;
    try {
      await http.delete(`/workspaces/${ws.id}/logo`);
      setLogoUrl("");
      toast.success("Logo removed");
      await reload();
    } catch (e) { toast.error(errText(e)); }
  };

  if (!ws) return null;
  return (
    <Panel cls="p-5 sm:p-6 space-y-5" testId="brand-editor">
      <div>
        <Eyebrow>Workspace brand</Eyebrow>
        <h3 className="font-display text-2xl sm:text-3xl mt-1">Rename this house, give it a face</h3>
        <p className="text-xs text-muted-foreground mt-1">Changes to the name, tagline and logo are visible across every page of this workspace. Only the workspace owner can edit.</p>
      </div>

      {/* Logo drop */}
      <div className="flex items-center gap-4" data-testid="brand-logo-row">
        <div className="w-20 h-20 rounded-2xl bg-black/40 border border-white/15 grid place-items-center overflow-hidden shrink-0" style={{ borderColor: `${accent}44` }}>
          {logoUrl ? <img src={logoUrl} alt="logo" className="w-full h-full object-contain" data-testid="brand-logo-current" /> : <span className="font-display text-3xl" style={{ color: accent }}>{(name || "M").slice(0, 1).toUpperCase()}</span>}
        </div>
        <div className="flex-1 min-w-0 flex flex-wrap gap-2 items-center">
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="brand-logo-input" />
          <button onClick={() => inputRef.current?.click()} disabled={!canEdit || uploading} data-testid="brand-logo-upload"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/15 text-xs font-mono uppercase tracking-wider hover:border-white/40 disabled:opacity-40">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {logoUrl ? "Replace logo" : "Upload logo"}
          </button>
          {logoUrl && <button onClick={removeLogo} disabled={!canEdit} data-testid="brand-logo-remove"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-xs font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground">
            <X className="w-3.5 h-3.5" /> Remove
          </button>}
          <span className="font-mono text-[10px] text-muted-foreground">PNG, JPG, WebP or SVG · up to 4MB</span>
        </div>
      </div>

      {/* Name */}
      <label className="block space-y-1.5">
        <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1.5"><Pencil className="w-3 h-3" />Workspace name</span>
        <input value={name} onChange={(e) => { setName(e.target.value); setDirty(true); }} disabled={!canEdit} data-testid="brand-name-input"
          className="w-full h-11 px-3 rounded-xl bg-white/[0.03] border border-white/10 text-sm outline-none focus:border-[#D4FF3A]/60" />
      </label>

      {/* Tagline */}
      <label className="block space-y-1.5">
        <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Tagline (optional)</span>
        <input value={tagline} onChange={(e) => { setTagline(e.target.value); setDirty(true); }} disabled={!canEdit} maxLength={140}
          placeholder="One short line that captures the brand" data-testid="brand-tagline-input"
          className="w-full h-11 px-3 rounded-xl bg-white/[0.03] border border-white/10 text-sm outline-none focus:border-[#D4FF3A]/60" />
      </label>

      {/* Accent */}
      <div className="space-y-1.5">
        <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1.5"><Palette className="w-3 h-3" />Accent color</span>
        <div className="flex items-center gap-2 flex-wrap">
          {ACCENTS.map((hex) => (
            <button key={hex} onClick={() => { setAccent(hex); setDirty(true); }} disabled={!canEdit}
              data-testid={`brand-accent-${hex}`} title={hex}
              className={`w-8 h-8 rounded-full border-2 transition-transform ${accent === hex ? "scale-110 border-white" : "border-white/10 hover:scale-105"}`}
              style={{ background: hex }}>
              {accent === hex && <Check className="w-4 h-4 text-black mx-auto" />}
            </button>
          ))}
          <input type="color" value={accent} onChange={(e) => { setAccent(e.target.value); setDirty(true); }} disabled={!canEdit}
            data-testid="brand-accent-custom" className="w-8 h-8 rounded-full border-2 border-white/10 bg-transparent cursor-pointer" />
          <span className="font-mono text-[10px] text-muted-foreground">{accent}</span>
        </div>
      </div>

      <div className="flex items-center gap-2 pt-2 border-t border-white/[0.06]">
        <button onClick={save} disabled={!canEdit || !dirty || saving || !name.trim()} data-testid="brand-save"
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-[#D4FF3A] text-black text-xs font-mono uppercase tracking-wider disabled:opacity-40">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}Save changes
        </button>
        {!canEdit && <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Owner only</span>}
      </div>
    </Panel>
  );
}
